import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "../../../components/ui/Badge";
import { Skeleton } from "../../../components/ui/Skeleton";
import TicketCard from "./TicketCard";
import { TICKET_COLUMNS, columnFor } from "./ticketModel";

/**
 * TicketBoard — Kanban de tickets agrupados por columna derivada.
 * Props:
 *  - incidents: array de incidencias (back)
 *  - loading: bool
 *  - localColumns: { [incidentId]: columnKey } overrides optimistas de front
 *  - onOpen(incident): abre detalle
 *  - onMoveColumn(incident, columnKey): mueve de columna (front-only)
 */
export default function TicketBoard({
  incidents,
  loading,
  localColumns = {},
  isMobile = false,
  onOpen,
  onMoveColumn,
}) {
  const { t } = useTranslation();
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const colLabel = (key) =>
    ({
      nuevo: t("Nuevo"),
      en_proceso: t("En proceso"),
      esperando: t("Esperando"),
      resuelto: t("Resuelto"),
      cerrado: t("Cerrado"),
    }[key] || key);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {TICKET_COLUMNS.map((col) => (
          <div key={col.key} className="rounded-xl bg-bg-muted/60 p-3 space-y-2">
            <Skeleton className="h-5 w-24 mb-3" />
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-28 w-full" />
          </div>
        ))}
      </div>
    );
  }

  // Agrupa por columna derivada (respetando overrides locales).
  const grouped = TICKET_COLUMNS.reduce((acc, c) => {
    acc[c.key] = [];
    return acc;
  }, {});
  incidents.forEach((inc) => {
    const key = columnFor(inc, localColumns[inc._id]);
    (grouped[key] || grouped.nuevo).push(inc);
  });

  const handleDrop = (colKey) => {
    if (dragId) {
      const inc = incidents.find((i) => i._id === dragId);
      if (inc) onMoveColumn?.(inc, colKey);
    }
    setDragId(null);
    setOverCol(null);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
      {TICKET_COLUMNS.map((col) => {
        const items = grouped[col.key] || [];
        const isOver = overCol === col.key;
        return (
          <section
            key={col.key}
            aria-label={colLabel(col.key)}
            onDragOver={(e) => {
              e.preventDefault();
              if (overCol !== col.key) setOverCol(col.key);
            }}
            onDragLeave={(e) => {
              // solo limpia si salimos del contenedor de la columna
              if (!e.currentTarget.contains(e.relatedTarget)) setOverCol(null);
            }}
            onDrop={() => handleDrop(col.key)}
            className={[
              "rounded-xl p-2.5 flex flex-col min-h-[180px] transition-colors",
              isOver
                ? "bg-brand-50 ring-2 ring-brand-300 ring-inset"
                : "bg-bg-muted/60",
            ].join(" ")}
          >
            <div className="flex items-center justify-between gap-2 px-1 mb-3">
              <Badge tone={col.tone} dot>
                {colLabel(col.key)}
              </Badge>
              <span className="text-xs font-semibold text-fg-muted tabular-nums">
                {items.length}
              </span>
            </div>

            <div className="space-y-2 flex-1">
              {items.length === 0 ? (
                <p className="text-center text-xs text-fg-subtle py-8 italic">
                  {isOver ? t("Soltar aquí") : t("Sin tickets")}
                </p>
              ) : (
                items.map((inc) => (
                  <TicketCard
                    key={inc._id}
                    incident={inc}
                    onClick={() => onOpen?.(inc)}
                    draggable={!isMobile}
                    dragging={dragId === inc._id}
                    onDragStart={(e) => {
                      setDragId(inc._id);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragEnd={() => {
                      setDragId(null);
                      setOverCol(null);
                    }}
                  />
                ))
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
